import React from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { StructuredListing } from '@hz-design-system/web-ui';
import { seoUrlPart } from '@ecg-internal/aurora-js-formatters';

import formatPrice from '../../utils/formatPrice';
import { getLargeImageUrl } from '../../utils/getImageUrls';
import { getGATrackingAttrForSSR, trackGAEvent } from '../../utils/gaTracking';
import { priceTypes } from '../../../../common/constants';

import { TSellerOtherItem } from 'types/TSellerOtherItem';
import { GA_TRACK_EVENTS_ACTIONS } from '../../utils/constants';

type TSellerItemProps = {
  item: TSellerOtherItem;
  className?: string;
  suppressZeroCents?: boolean;
  index: number;
};

const SELLER_OTHER_ITEMS_CAROUSEL_LABEL = 'SellerOtherItemsCarousel';

const formatGATrackingAttr = (item: TSellerOtherItem): object =>
  getGATrackingAttrForSSR(
    'seller_own_ads',
    `${item.category.parentId}_${seoUrlPart(item.category.parentName)}/${item.category.id}_${seoUrlPart(
      item.category.fullName,
    )}`,
  );

const SellerItem = ({ item, className, suppressZeroCents, index }: TSellerItemProps) => {
  const { t } = useI18nContext();
  const imageUrl = item.imageUrls?.[0];
  const formattedImageUrl = imageUrl && imageUrl.trim() ? getLargeImageUrl(imageUrl, item.imageSizes) : '';

  const isCurrency = [priceTypes.MIN_BID, priceTypes.FIXED].includes(item.price.priceType);
  const isPricePerMonth = item.isLease && isCurrency;
  const formattedPrice = formatPrice(item.price, false, t, suppressZeroCents);

  const handleClick = () => {
    trackGAEvent(GA_TRACK_EVENTS_ACTIONS.Click, SELLER_OTHER_ITEMS_CAROUSEL_LABEL, {
      value: index + 1,
    });
  };

  return (
    <StructuredListing
      className={className}
      price={formattedPrice}
      title={item.title}
      imageSrc={formattedImageUrl || ''}
      alt={`${item.title} ${t('sellerOtherItems.imageAltText')}`}
      imageLoading="lazy"
      decoding="async"
      isMaskedLink={item.thinContent}
      linkProps={{ href: item.url, onClick: handleClick, ...formatGATrackingAttr(item) }}
      {...(isPricePerMonth && {
        pricePrefix: t('listing.leaseprice.prefix'),
        priceSuffix: t('listing.leaseprice.suffix'),
      })}
    />
  );
};

export default SellerItem;
